import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { memo, useCallback, useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import Loading from "../../../components/Loading";
import NoData from "../../../components/UI/NoData";
import useFetch from "../../../hooks/useFetch";
import { ITicketAutomationReceivedRule } from "../../../services/tasySchedMenu.service";
import { ScheduleAppointmentService } from "../../Schedule/appointment.service";
import { ETasyScheduleTypeCode } from "../../Schedule/model";
import { TAppointment } from "../../Schedule/NewSchedule/schema";
import { Calendar } from "../../Schedule/Schedule/Calendar";
import { Type } from "../../Schedule/Schedule/Type";
import { useTranslation } from "react-i18next";

interface IReturnAvailabilityProps {
  rule?: ITicketAutomationReceivedRule | null;
}

interface IReturnAppointment {
  identifier: number;
  pacientName: string;
  physicianNameExecute: string;
  calendarName: string;
  descriptionSite: string | null;
  calendarDate: string;
  calendarStartDate: string;
  calendarStatusName: string;
  tasyScheduleTypeName: string;
  calendarIdentifier: number;
  medicalSpecialtyCode: number;
  medicalSpecialtyName: string;
  insuranceCode?: number;
  insuranceName?: string;
  insuranceCategoryCode?: string;
  insuranceCategoryName?: string;
}

const getDescription = (appointment: IReturnAppointment) =>
  appointment.descriptionSite?.trim() ||
  [appointment.physicianNameExecute, appointment.medicalSpecialtyName]
    .filter(Boolean)
    .join(" - ") ||
  appointment.calendarName;

const ReturnAvailability: React.FC<IReturnAvailabilityProps> = ({ rule }) => {
  const { t } = useTranslation();

  const [appointments, setAppointments] = useState<IReturnAppointment[]>([]);

  const [selectedIdentifier, setSelectedIdentifier] = useState<number | null>(
    null,
  );

  const { getValues, setValue } = useFormContext<TAppointment>();

  const { sendRequest, loading } = useFetch(
    ScheduleAppointmentService.getCalendarReturnAvaiablesTasy2,
  );

  const fetchAppointments = useCallback(async () => {
    const { patient, establishment } = getValues() as any;
    if (!rule?.id || !patient?.individualId) return;

    const { data, success } = await sendRequest({
      IndividualId: patient.individualId || patient.id,
      TicketAutomationReceivedRuleId: rule.id,
      EstablishmentCode: establishment?.integrationId,
    });

    if (success && data) {
      setAppointments(data);
    }
  }, [getValues, rule?.id, sendRequest]);

  const selectAppointment = useCallback(
    (appointment: IReturnAppointment) => {
      setSelectedIdentifier(appointment.identifier);
      setValue("search", {
        type: ETasyScheduleTypeCode.CONSULTATION,
        speciality: {
          id: String(appointment.medicalSpecialtyCode),
          name: appointment.medicalSpecialtyName,
        },
        professional: appointment.calendarIdentifier
          ? {
              id: String(appointment.calendarIdentifier),
            }
          : null,
        date: new Date(),
        period: [],
      });
      setValue("insurance", {
        id: String(appointment.insuranceCode || ""),
        code: String(appointment.insuranceCode || ""),
        insuranceIntegrationId: String(appointment.insuranceCode || ""),
        name: appointment.insuranceCategoryName
          ? `${appointment.insuranceName} (${appointment.insuranceCategoryName})`
          : appointment.insuranceName,
        insuranceName: appointment.insuranceName,
        categoryIntegrationId: appointment.insuranceCategoryCode,
        categoryName: appointment.insuranceCategoryName,
      } as any);
    },
    [setValue],
  );

  useEffect(() => {
    fetchAppointments();
  }, [fetchAppointments]);

  if (loading) return <Loading />;

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 4 }}>
        <Typography variant="h6">
          {t("page.schedules.newSchedule.search.return.title")}
        </Typography>
      </Box>

      {!appointments.length ? (
        <NoData message="Nenhuma agenda disponivel para retorno" />
      ) : (
        <TableContainer sx={{ maxHeight: 430 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>
                  {t("page.schedules.newSchedule.search.return.date")}
                </TableCell>
                <TableCell>
                  {t("page.schedules.newSchedule.search.return.patient")}
                </TableCell>
                <TableCell>
                  {t("page.schedules.newSchedule.search.return.description")}
                </TableCell>
                <TableCell>
                  {t("page.schedules.newSchedule.search.return.status")}
                </TableCell>
                <TableCell>
                  {t("page.schedules.newSchedule.search.return.type")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {appointments.map((appointment) => (
                <TableRow
                  hover
                  key={appointment.identifier}
                  selected={selectedIdentifier === appointment.identifier}
                  onClick={() => selectAppointment(appointment)}
                  sx={{ cursor: "pointer" }}
                >
                  <TableCell>
                    <Calendar
                      date={
                        appointment.calendarStartDate ||
                        appointment.calendarDate
                      }
                    />
                  </TableCell>
                  <TableCell>{appointment.pacientName}</TableCell>
                  <TableCell>{getDescription(appointment)}</TableCell>
                  <TableCell>{appointment.calendarStatusName}</TableCell>
                  <TableCell>
                    <Type type={ETasyScheduleTypeCode.CONSULTATION} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default memo(ReturnAvailability);
